// @ts-nocheck
import { useState, useCallback, useEffect, useRef } from "react"
import { useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import type { GetContractResponseContent } from "src/types/contract.types"

const INITIAL_CONTENT = `
<h2>RECORDING ARTIST AGREEMENT</h2>

<p>This Recording Artist Agreement ("Agreement") is entered into as of January 15, 2024, between <strong>Midnight Records LLC</strong>, a Delaware limited liability company with offices at 123 Music Row, Nashville, Tennessee 37203 ("Label"), and <strong>Alexandra Rivers</strong>, an individual residing at 456 Sunset Boulevard, Los Angeles, California 90028 ("Artist").</p>

<br />

<h3>1. RECORDING COMMITMENT</h3>
<p>Artist agrees to record and deliver to Label, and Label agrees to accept, a minimum of <strong>twelve (12) master recordings</strong> ("Masters") suitable for commercial release during the Initial Term. The Masters shall be recorded at studios approved by Label and shall be of a quality and character consistent with Artist's prior recordings and commercially acceptable to Label.</p>

<br />

<h3>2. TERM</h3>
<p>The term of this Agreement ("Term") shall commence on the date hereof and shall continue for an initial period of <strong>three (3) years</strong> ("Initial Term"), unless earlier terminated in accordance with the provisions hereof. Label shall have the option to extend the Term for up to two (2) additional one-year periods ("Option Periods") upon written notice to Artist.</p>

<br />

<h3>3. ROYALTIES</h3>
<p>Label shall pay Artist royalties in the amount of <strong>fifteen percent (15%)</strong> of the suggested retail list price for all records sold through normal retail channels in the United States and Canada. For sales outside the United States and Canada, Artist shall receive <strong>twelve percent (12%)</strong> of the suggested retail list price. Royalties shall be calculated on records sold and paid for, less returns, reserves, and deductions for packaging, free goods, and promotional copies.</p>

<br />

<h3>4. ADVANCES</h3>
<p>Label agrees to pay Artist a non-recoupable advance of <strong>$50,000</strong> upon execution of this Agreement. An additional advance of <strong>$25,000</strong> shall be payable upon delivery and acceptance of the first album of Masters. All advances shall be recoupable from royalties earned hereunder.</p>

<br />

<h3>5. MASTER RECORDINGS</h3>
<p>All Masters recorded hereunder shall be the sole and exclusive property of Label in perpetuity throughout the universe. Label shall have the exclusive right to manufacture, distribute, sell, license, and exploit the Masters in all formats, whether now known or hereafter devised, including but not limited to physical formats, digital downloads, streaming, and synchronization uses.</p>

<br />

<h3>6. ARTIST'S WARRANTIES</h3>
<p>Artist hereby warrants and represents that: (a) Artist is free to enter into this Agreement and is not bound by any agreement that would conflict with the terms hereof; (b) Artist has the right to grant all rights granted to Label hereunder; (c) all Masters delivered hereunder will be original compositions or properly licensed; and (d) Artist will not record any Masters for any other person or entity during the Term without Label's prior written consent.</p>

<br />

<h3>7. MARKETING AND PROMOTION</h3>
<p>Label agrees to use commercially reasonable efforts to promote and market the Masters, including but not limited to radio promotion, press and publicity, social media marketing, and tour support. Label shall have the right to approve all artwork, liner notes, and marketing materials related to the Masters.</p>

<br />

<h3>8. TERMINATION</h3>
<p>Either party may terminate this Agreement upon written notice if the other party materially breaches any provision hereof and fails to cure such breach within thirty (30) days after written notice thereof. Upon termination, Label shall retain all rights in Masters previously delivered and accepted.</p>
`

const VARIABLE_OPEN_TAG_REGEX = /<variable\b[^>]*>/gi
const VARIABLE_CLOSE_TAG_REGEX = /<\/variable>/gi
const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")

const stripVariableTags = (value: string): string =>
  value
    .replace(VARIABLE_OPEN_TAG_REGEX, "")
    .replace(VARIABLE_CLOSE_TAG_REGEX, "")

const convertPlainTextToHtml = (value: string): string => {
  const normalized = value.replace(/\r\n/g, "\n").trim()
  if (!normalized) return ""

  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph.trim())}</p>`)
    .join("\n")
}

const calculateWordCount = (text: string): number =>
  text.trim().split(/\s+/).filter((word) => word.length > 0).length

/**
 * True when the contract represents an ANALYZED real deal or one backed by
 * a real uploaded PDF — i.e. it is NOT a genuinely-empty NEW contract.
 *
 * The Midnight Records INITIAL_CONTENT placeholder is a hand-authored demo
 * stub. It must NEVER render for a real deal: a real deal's content lives in
 * the PDF (viewer mode) and its extracted facts live in the evidence panel,
 * so an empty editor here is honest — fiction is not. We detect "real deal"
 * by any of the analysis/PDF signals the backend attaches to the response
 * (read via a boundary cast — none of these are on the api-model stub yet,
 * mirroring the pdfUrl cast in contract-editor.tsx).
 */
const isAnalyzedOrPdfBackedContract = (
  contract?: GetContractResponseContent | null
): boolean => {
  if (!contract) return false
  const c = contract as {
    pdfUrl?: unknown
    eqCardsRaw?: unknown
    eqData?: unknown
    dealAnalysisId?: unknown
    analysisStatus?: unknown
  }
  return (
    typeof c.pdfUrl === "string" && c.pdfUrl.length > 0 ||
    (c.eqCardsRaw !== undefined && c.eqCardsRaw !== null) ||
    (c.eqData !== undefined && c.eqData !== null) ||
    (typeof c.dealAnalysisId === "string" && c.dealAnalysisId.length > 0) ||
    (typeof c.analysisStatus === "string" && c.analysisStatus.length > 0)
  )
}

export const getContractEditorInitialContent = (
  contract?: GetContractResponseContent | null
): string => {
  const taggedText = contract?.contractExtraction?.contractTexts?.taggedText?.text ?? ""
  const originalText = contract?.contractExtraction?.contractTexts?.originalText?.text ?? ""
  const sourceText = (taggedText || originalText).trim()

  // KILL THE FICTION: an analyzed real deal (or a PDF-backed contract) with
  // no editor text gets an EMPTY editor, never the Midnight Records demo
  // stub. The real content surface for these is the PDF viewer + evidence
  // panel. Only a genuinely-empty NEW contract falls through to the
  // placeholder, which is the one case it is meant for.
  const emptyFallback = isAnalyzedOrPdfBackedContract(contract) ? "" : INITIAL_CONTENT

  if (!sourceText) {
    return emptyFallback
  }

  const normalizedText = stripVariableTags(sourceText).trim()
  if (!normalizedText) {
    return emptyFallback
  }

  if (HTML_TAG_REGEX.test(normalizedText)) {
    return normalizedText
  }

  return convertPlainTextToHtml(normalizedText)
}

interface UseContractEditorOptions {
  initialContent?: string
  editable?: boolean
  onTextChange?: (text: string) => void
}

export function useContractEditor({
  initialContent = INITIAL_CONTENT,
  editable = true,
  onTextChange,
}: UseContractEditorOptions = {}) {
  const [wordCount, setWordCount] = useState(0)
  const previousInitialContentRef = useRef(initialContent)
  const onTextChangeRef = useRef(onTextChange)

  useEffect(() => {
    onTextChangeRef.current = onTextChange
  }, [onTextChange])

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: initialContent,
    editable,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "focus:outline-none",
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText({ blockSeparator: "\n\n" })
      setWordCount(calculateWordCount(text))
      onTextChangeRef.current?.(text)
    },
    onCreate: ({ editor }) => {
      const text = editor.getText({ blockSeparator: "\n\n" })
      setWordCount(calculateWordCount(text))
      onTextChangeRef.current?.(text)
    },
  })

  useEffect(() => {
    if (!editor) return
    editor.setEditable(editable)
  }, [editor, editable])

  useEffect(() => {
    if (!editor) return
    if (previousInitialContentRef.current === initialContent) return

    editor.commands.setContent(initialContent, { emitUpdate: false })
    setWordCount(calculateWordCount(editor.getText()))
    previousInitialContentRef.current = initialContent
  }, [editor, initialContent])

  const toggleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run()
  }, [editor])

  const toggleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run()
  }, [editor])

  const toggleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run()
  }, [editor])

  return {
    editor,
    wordCount,
    toggleBold,
    toggleItalic,
    toggleUnderline,
  }
}
